/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  generateTrackingId,
  type V1SetActiveAccountSetActiveAccountResponse,
} from "@farmlands/myfarmlands-api";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { myFarmlandsApi } from "../../../services/myfarmlands-api";

export type UseActiveAccountQueryResult = {
  /** `setActiveAccount` response (account/contact-person details), or `null` until resolved. */
  activeAccountDetails: V1SetActiveAccountSetActiveAccountResponse | null;
  /** `true` once the active account has been set for this account number. */
  isReady: boolean;
  /** Error from the `setActiveAccount` call, if any. */
  error: AxiosError | null;
};

export const getActiveAccountQueryKey = (accountNumber: string | undefined) =>
  ["activeAccount", accountNumber] as const;

export const useActiveAccountQuery = (
  accountNumber: string | null
): UseActiveAccountQueryResult => {
  const { data, isSuccess, isFetching, error } = useQuery<
    V1SetActiveAccountSetActiveAccountResponse,
    AxiosError
  >({
    queryKey: getActiveAccountQueryKey(accountNumber ?? undefined),
    queryFn: async () => {
      const response = await myFarmlandsApi.accounts.setActiveAccount({
        accountNumber: accountNumber as string,
        trackingID: generateTrackingId(),
      });
      return response.data;
    },
    enabled: !!accountNumber,
    staleTime: 0,
  });

  return {
    activeAccountDetails: data ?? null,
    isReady: isSuccess && !isFetching,
    error: error ?? null,
  };
};
